import { FC, useMemo, useCallback, useEffect } from 'react'

import { List, Pagination, EmptyList, SafetyCaseHazardCard } from '../../../components'
import { ISafetyCaseHazard, IActionOption, IRefs } from '../../../interfaces'
import { useNavigate } from 'react-router-dom'

type SafetyCaseHazardCardsListProps = {
  pagination: { pageSize: number; pageIndex: number }
  setPagination: (values: { [key: string]: number }) => void
  hazards: ISafetyCaseHazard[] | undefined
  hazardsRefs: IRefs
  actionsButtonOptions?: IActionOption<ISafetyCaseHazard>[]
  isLoading: boolean
  activeHazardId?: number
}

const SafetyCaseHazardCardsList: FC<SafetyCaseHazardCardsListProps> = props => {
  const { hazards, pagination, setPagination, actionsButtonOptions, isLoading, activeHazardId } = props

  const navigate = useNavigate()

  const handleNavigateToHazard = useCallback((hazard: ISafetyCaseHazard) => navigate(`${hazard.id}`), [navigate])

  const totalItemsCount = hazards?.length || 0

  const { pageIndex, pageSize } = pagination

  const setPageSize = useCallback((pageSize: number) => setPagination({ pageSize }), [setPagination])

  const setPageIndex = useCallback((pageIndex: number) => setPagination({ pageIndex }), [setPagination])

  const totalPageCount = useMemo(() => Math.ceil(totalItemsCount / pageSize), [totalItemsCount, pageSize])

  const hazardsPage = useMemo(
    () => (hazards || []).slice(pageIndex * pageSize, pageIndex * pageSize + pageSize),
    [hazards, pageIndex, pageSize],
  )

  useEffect(() => {
    if (pageIndex > totalPageCount) {
      setPageIndex(totalPageCount)
    }
  }, [pageIndex, setPageIndex, totalPageCount])

  const canNextPage = pageIndex < totalPageCount

  const canPreviousPage = pageIndex > 0

  return (
    <>
      <div className="overflow-auto h-100">
        <List
          data={hazardsPage}
          ListItem={SafetyCaseHazardCard}
          noDataContent={<EmptyList>No Safety Hazards found.</EmptyList>}
          actionsButtonOptions={actionsButtonOptions}
          onTitleClick={handleNavigateToHazard}
          activeId={activeHazardId}
          isLoading={isLoading}
        />
      </div>

      <Pagination
        pageIndex={pageIndex}
        setPageIndex={setPageIndex}
        pageSize={pageSize}
        setPageSize={setPageSize}
        totalItemsCount={totalItemsCount}
        totalPageCount={totalPageCount}
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
      />
    </>
  )
}

export default SafetyCaseHazardCardsList
